import type { RuleModule } from '../engine/registry';
import { OK, reject, type GameState } from '../engine/state';
import { enterPhase, openWindow } from '../engine/phase';
import { rotateTurn } from './turnFlow';
import type { SeatIdx } from '../types';

// Paired turns (outline §13.2; plan §7.6): the current official 5–6 seat rule. Two
// markers circulate — after the roller's full turn, the partner (the THIRD live seat to
// the roller's left) takes an ACTION-only half: build/buy/play-1-card/supply-trade, no
// rolling, no player trade (those validators already scope themselves out). The partner
// can win — PARTNER_ACTION is win-eligible. This module REPLACES turn-flow's END_TURN
// (ordered override); the card allowance is per seat-half via openWindow at hand-off.

/** The third non-resigned seat clockwise from the roller (cyclic; roller excluded). */
export function partnerOf(state: GameState, roller: SeatIdx): SeatIdx | null {
  const n = state.seats.length;
  const live: SeatIdx[] = [];
  for (let step = 1; step < n; step++) {
    const s = (roller + step) % n;
    if (!state.seats[s]!.resigned) live.push(s);
  }
  if (live.length === 0) return null;
  return live[(3 - 1) % live.length]!; // "roller+3" counted over the live ring (plan §7.6)
}

export const pairedTurnsModule: RuleModule = {
  id: 'paired-turns',
  register(ctx) {
    ctx.phaseState('PARTNER_ACTION', {
      eligibleWinner: (n) => (n.t === 'PARTNER_ACTION' ? n.seat : null), // the partner CAN win (outline §13.2)
      timeoutKey: 'PARTNER_ACTION',
    });

    ctx.action('END_TURN', {
      enumerate(state, _env, seat) {
        const p = state.phase;
        return (p.t === 'ACTION' || p.t === 'PARTNER_ACTION') && p.seat === seat ? [{}] : null;
      },
      validate(state, action) {
        const p = state.phase;
        if (p.t !== 'ACTION' && p.t !== 'PARTNER_ACTION') return reject('WRONG_PHASE');
        if (p.seat !== action.seat) return reject('NOT_YOUR_TURN');
        return OK;
      },
      apply(state, _action, env, emit) {
        if (state.phase.t === 'ACTION') {
          const roller = state.phase.seat;
          const partner = partnerOf(state, roller);
          if (partner === null || partner === roller) {
            rotateTurn(state, env, emit); // nobody left for a half — straight rotation
            return;
          }
          enterPhase(state, env, { t: 'PARTNER_ACTION', roller, seat: partner }, emit);
          emit({ kind: 'WINDOW_OPENED', vis: 'public', window: 'PARTNER', seat: partner });
          openWindow(state, env, partner); // the partner's own 1-card allowance (plan §7.6)
        } else {
          rotateTurn(state, env, emit); // partner half done → both markers shift
        }
      },
    });
  },
};
